import React, { useState, useRef, useEffect } from 'react';
import './FaleConoscoPage.css';

const SUBJECTS = [
  'Dúvida sobre o sistema',
  'Problema técnico',
  'Sugestão de melhoria',
  'Solicitação de dados',
  'Parceria institucional',
  'Outro',
];

const CONTACT_INFO = [
  {
    icon: '✉️',
    label: 'E-mail',
    value: 'saneamentovivo@ins.gov.br',
    link: 'mailto:saneamentovivo@ins.gov.br',
  },
  {
    icon: '🌐',
    label: 'Portal',
    value: 'www.saneamentovivo.gov.br',
    link: null,
  },
  {
    icon: '🏛️',
    label: 'Instituição',
    value: 'Secretaria de Infraestrutura e Saneamento',
    link: null,
  },
];

function SuccessState({ onReset }) {
  const btnRef = useRef(null);
  useEffect(() => { btnRef.current?.focus(); }, []);

  return (
    <div className="fale-success" role="status" aria-live="polite">
      <div className="fale-success__icon" aria-hidden="true">✅</div>
      <h2 className="fale-success__title">Mensagem enviada!</h2>
      <p className="fale-success__text">
        Obrigado pelo contato. Nossa equipe responderá em até 5 dias úteis.
      </p>
      <button ref={btnRef} className="fale-btn fale-btn--primary" onClick={onReset}>
        Enviar outra mensagem
      </button>
    </div>
  );
}

export default function FaleConoscoPage({ onNavigate }) {
  const [form, setForm] = useState({
    name: '', email: '', subject: '', message: '',
  });
  const [errors, setErrors] = useState({});
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  function validate() {
    const e = {};
    if (!form.name.trim())    e.name    = 'Informe seu nome.';
    if (!form.email.trim())   e.email   = 'Informe seu e-mail.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'E-mail inválido.';
    if (!form.subject)        e.subject = 'Selecione um assunto.';
    if (!form.message.trim()) e.message = 'Escreva sua mensagem.';
    else if (form.message.trim().length < 20) e.message = 'Mensagem muito curta (mínimo 20 caracteres).';
    return e;
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
    if (errors[name]) setErrors(er => ({ ...er, [name]: undefined }));
  }

  function handleSubmit(e) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      /* Foca no primeiro campo com erro */
      const firstKey = Object.keys(errs)[0];
      document.getElementById(`fale-${firstKey}`)?.focus();
      return;
    }
    setSubmitting(true);
    /* Simula envio assíncrono */
    setTimeout(() => {
      setSent(true);
      setSubmitting(false);
    }, 900);
  }

  function handleReset() {
    setForm({ name: '', email: '', subject: '', message: '' });
    setErrors({});
    setSent(false);
  }

  return (
    <main className="fale-page">
      <div className="fale-container">

        <header className="fale-page-header">
          <button
            className="fale-back-btn"
            onClick={() => onNavigate('home')}
            aria-label="Voltar ao mapa principal"
          >
            ← Voltar ao Mapa
          </button>
          <div className="fale-header-text">
            <h1 className="fale-page-title">Fale Conosco</h1>
            <p className="fale-page-sub">
              Tem dúvidas, sugestões ou precisa de suporte? Nossa equipe está pronta para ajudar.
            </p>
          </div>
        </header>

        <div className="fale-layout">

          {/* ── Formulário ─────────────────────────────── */}
          <div className="fale-form-wrap">
            {sent ? (
              <SuccessState onReset={handleReset} />
            ) : (
              <form
                className="fale-form"
                onSubmit={handleSubmit}
                noValidate
                aria-label="Formulário de contato"
              >
                {/* Nome */}
                <div className={`fale-field${errors.name ? ' fale-field--error' : ''}`}>
                  <label htmlFor="fale-name" className="fale-label">
                    Nome <span aria-hidden="true" className="fale-required">*</span>
                  </label>
                  <input
                    id="fale-name"
                    name="name"
                    type="text"
                    className="fale-input"
                    value={form.name}
                    onChange={handleChange}
                    autoComplete="name"
                    aria-required="true"
                    aria-describedby={errors.name ? 'fale-name-err' : undefined}
                    aria-invalid={!!errors.name}
                    placeholder="Seu nome completo"
                  />
                  {errors.name && (
                    <span id="fale-name-err" className="fale-error-msg" role="alert">
                      {errors.name}
                    </span>
                  )}
                </div>

                {/* E-mail */}
                <div className={`fale-field${errors.email ? ' fale-field--error' : ''}`}>
                  <label htmlFor="fale-email" className="fale-label">
                    E-mail <span aria-hidden="true" className="fale-required">*</span>
                  </label>
                  <input
                    id="fale-email"
                    name="email"
                    type="email"
                    className="fale-input"
                    value={form.email}
                    onChange={handleChange}
                    autoComplete="email"
                    aria-required="true"
                    aria-describedby={errors.email ? 'fale-email-err' : undefined}
                    aria-invalid={!!errors.email}
                    placeholder="seu@email.com.br"
                  />
                  {errors.email && (
                    <span id="fale-email-err" className="fale-error-msg" role="alert">
                      {errors.email}
                    </span>
                  )}
                </div>

                {/* Assunto */}
                <div className={`fale-field${errors.subject ? ' fale-field--error' : ''}`}>
                  <label htmlFor="fale-subject" className="fale-label">
                    Assunto <span aria-hidden="true" className="fale-required">*</span>
                  </label>
                  <select
                    id="fale-subject"
                    name="subject"
                    className="fale-select"
                    value={form.subject}
                    onChange={handleChange}
                    aria-required="true"
                    aria-describedby={errors.subject ? 'fale-subject-err' : undefined}
                    aria-invalid={!!errors.subject}
                  >
                    <option value="">Selecione um assunto...</option>
                    {SUBJECTS.map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                  {errors.subject && (
                    <span id="fale-subject-err" className="fale-error-msg" role="alert">
                      {errors.subject}
                    </span>
                  )}
                </div>

                {/* Mensagem */}
                <div className={`fale-field${errors.message ? ' fale-field--error' : ''}`}>
                  <label htmlFor="fale-message" className="fale-label">
                    Mensagem <span aria-hidden="true" className="fale-required">*</span>
                  </label>
                  <textarea
                    id="fale-message"
                    name="message"
                    className="fale-textarea"
                    rows={6}
                    value={form.message}
                    onChange={handleChange}
                    aria-required="true"
                    aria-describedby={`fale-msg-hint${errors.message ? ' fale-message-err' : ''}`}
                    aria-invalid={!!errors.message}
                    placeholder="Descreva sua dúvida ou sugestão..."
                  />
                  <span id="fale-msg-hint" className="fale-hint">
                    {form.message.length} caracteres
                  </span>
                  {errors.message && (
                    <span id="fale-message-err" className="fale-error-msg" role="alert">
                      {errors.message}
                    </span>
                  )}
                </div>

                <p className="fale-required-note">
                  <span aria-hidden="true">*</span> Campos obrigatórios
                </p>

                <button
                  type="submit"
                  className={`fale-btn fale-btn--primary fale-btn--submit${submitting ? ' fale-btn--loading' : ''}`}
                  disabled={submitting}
                  aria-busy={submitting}
                >
                  {submitting ? 'Enviando...' : 'Enviar Mensagem'}
                </button>
              </form>
            )}
          </div>

          {/* ── Mascote lateral ────────────────────────── */}
          <aside className="fale-info" aria-hidden="true">
            <div className="fale-info-mascote">
              <img src="/mascote.png" alt="" className="fale-guto" />
              <div className="fale-guto-bubble">Pode contar comigo!</div>
            </div>
          </aside>

        </div>
      </div>
    </main>
  );
}
